import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { Helmet } from 'react-helmet-async';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import queryString from 'query-string';
import ttt from '../../tracking';

import Layout from '../../shared/Layout';
import ContactForm from '../components/ContactForm';
import Loading from '../components/Loading';
import { fetchConfig as fetchConfigAction, submitForm as submitFormAction } from '../rest';
import { updateFields as updateFieldsAction } from '../state/actions';
import {
  getUser,
  getTopics,
  getSelectedTopicSubjects,
  getSelectedSubjectForm,
  mustShowSubmit,
  isFormValid as isFormValidSelector,
  getFileUploadEnabled,
} from '../state/selectors';

class ContactPage extends Component {
  componentDidMount() {
    const {
      location: { search },
      fetchConfig,
    } = this.props;
    const queryValues = queryString.parse(search);
    const { filterSubject } = queryValues;
    fetchConfig(`?filterSubject=${filterSubject}`);
    ttt.pageview('wbm/Help', 'wbm/Help/support/contact');
  }

  componentDidUpdate({ fields: prevFields }) {
    const {
      updateFields,
      submitSuccess,
      history: { push },
      match: {
        params: { topic = '', subject = '' },
      },
    } = this.props;

    if (topic !== prevFields.topic || subject !== prevFields.subject) {
      updateFields({ topic, subject });
    }

    if (submitSuccess) {
      ttt.pageview('wbm/Help', 'wbm/Help/support/contact/success', {
        PropSearchListFilter: `PropSearchListFilter:help_contact_category_${topic};help_contact_subcategory_${subject}`,
      });
      push('/support/contact/success');

      if (window.scrollTo) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }

  updateLocation = ({ topic, subject }, { force = false } = {}) => {
    const {
      history: { push, replace },
    } = this.props;
    const path = ['/support/contact', topic, subject].filter((s) => !!s).join('/');

    if (force) {
      replace(path);
    } else {
      push(path);
    }
  };

  addFile = (uploadId) => {
    const { fields, updateFields } = this.props;
    const newFields = {
      ...fields,
      files: [...fields.files, uploadId],
    };
    updateFields(newFields);
  };

  removeFile = (uploadId) => {
    const { fields, updateFields } = this.props;
    const newFields = {
      ...fields,
      files: fields.files.filter((storedId) => storedId !== uploadId),
    };
    updateFields(newFields);
  };

  render() {
    const {
      fields,
      loading,
      submitting,
      config,
      errors,
      updateFields,
      submitForm,
      submitError,
      user,
      topics,
      subjects,
      subjectForm,
      showSubmit,
      showUpload,
      isFormValid,
    } = this.props;

    return (
      <Layout>
        <FormattedMessage id="CONTACT_FORM_TITLE">
          {(title) => <Helmet title={`${title} | XING`} />}
        </FormattedMessage>

        {(loading && <Loading />) || (
          <ContactForm
            fields={fields}
            config={config}
            errors={errors}
            updateFields={updateFields}
            updateLocation={this.updateLocation}
            submitForm={submitForm}
            submitError={submitError}
            submitting={submitting}
            user={user}
            topics={topics}
            subjects={subjects}
            subjectForm={subjectForm}
            showSubmit={showSubmit}
            submitDisabled={!isFormValid}
            addFile={this.addFile}
            removeFile={this.removeFile}
            showUpload={showUpload}
          />
        )}
      </Layout>
    );
  }
}

ContactPage.defaultProps = {
  config: {
    topics: [],
  },
  errors: {},
  fields: {
    email: '',
    name: '',
    message: '',
    topic: '',
    subject: '',
    recaptcha: '',
    files: [],
  },
  history: {
    push() {},
    replace() {},
  },
  isFormValid: false,
  loading: false,
  match: {
    params: {
      topic: '',
      subject: '',
    },
  },
  subjectForm: {
    type: '',
    placeholder: '',
  },
  subjects: [],
  submitting: false,
  submitError: false,
  submitSuccess: false,
  showSubmit: false,
  showUpload: false,
  topics: [],
  user: {
    name: '',
    email: '',
  },
  fetchConfig() {},
  submitForm() {},
  updateFields() {},
};

ContactPage.propTypes = {
  config: PropTypes.shape({
    topics: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
      }),
    ).isRequired,
  }),
  errors: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    subject: PropTypes.string,
    topic: PropTypes.string,
  }),
  fetchConfig: PropTypes.func,
  fields: PropTypes.shape({
    email: PropTypes.string,
    name: PropTypes.string,
    message: PropTypes.string,
    topic: PropTypes.string,
    subject: PropTypes.string,
    recaptcha: PropTypes.string,
    files: PropTypes.arrayOf(PropTypes.string),
  }),
  history: PropTypes.shape({
    push: PropTypes.func,
    replace: PropTypes.func,
  }),
  isFormValid: PropTypes.bool,
  loading: PropTypes.bool,
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      topic: PropTypes.string,
      subject: PropTypes.string,
    }),
  }),
  showSubmit: PropTypes.bool,
  showUpload: PropTypes.bool,
  subjectForm: PropTypes.shape({
    type: PropTypes.oneOf(['text', 'link', '']),
    placeholder: PropTypes.string,
  }),
  subjects: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  submitting: PropTypes.bool,
  submitSuccess: PropTypes.bool,
  submitError: PropTypes.bool,
  submitForm: PropTypes.func,
  topics: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
  updateFields: PropTypes.func,
  user: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
  }),
};

const mapStateToProps = ({ contact }) => ({
  ...contact,
  user: getUser(contact),
  topics: getTopics(contact),
  subjects: getSelectedTopicSubjects(contact),
  subjectForm: getSelectedSubjectForm(contact),
  showSubmit: mustShowSubmit(contact),
  isFormValid: isFormValidSelector(contact),
  showUpload: getFileUploadEnabled(contact),
});

export { ContactPage };
export default withRouter(
  connect(mapStateToProps, {
    fetchConfig: fetchConfigAction,
    updateFields: updateFieldsAction,
    submitForm: submitFormAction,
  })(ContactPage),
);
